import { create } from "zustand";
import { api, tokenStorage } from "@/lib/api";

export type BusinessType = { id: string; name: string; description?: string; icon?: string };

export type User = {
  id: string;
  full_name: string;
  email: string;
  phone_number?: string;
  business_types?: BusinessType[];
};

type AuthState = {
  user: User | null;
  loading: boolean;
  initialized: boolean;
  init: () => Promise<void>;
  login: (identifier: string, password: string) => Promise<User>;
  signup: (data: { full_name: string; email: string; phone_number: string; password: string }) => Promise<User>;
  logout: () => void;
  setUser: (u: User | null) => void;
};

export const useAuth = create<AuthState>((set) => ({
  user: null,
  loading: false,
  initialized: false,
  setUser: (u) => set({ user: u }),
  init: async () => {
    const token = tokenStorage.get();
    if (!token) {
      set({ initialized: true });
      return;
    }
    set({ loading: true });
    try {
      const { data } = await api.get("/api/auth/me");
      // Transform user_business_types to business_types for frontend compatibility
      const userData = data.user || data;
      if (userData?.user_business_types) {
        userData.business_types = userData.user_business_types.map((bt: any) => ({
          id: bt.business_types.id,
          name: bt.business_types.name,
          description: bt.business_types.description,
        }));
      }
      set({ user: userData, initialized: true, loading: false });
    } catch {
      tokenStorage.clear();
      set({ user: null, initialized: true, loading: false });
    }
  },
  login: async (identifier, password) => {
    const { data } = await api.post("/api/auth/login", { identifier, password });
    tokenStorage.set(data.tokens?.accessToken || data.accessToken);
    // Transform user_business_types to business_types for frontend compatibility
    const userData = data.user || data;
    if (userData?.user_business_types) {
      userData.business_types = userData.user_business_types.map((bt: any) => ({
        id: bt.business_types.id,
        name: bt.business_types.name,
        description: bt.business_types.description,
      }));
    }
    set({ user: userData });
    return userData;
  },
  signup: async (payload) => {
    const { data } = await api.post("/api/auth/signup", payload);
    tokenStorage.set(data.tokens?.accessToken || data.accessToken);
    // Transform user_business_types to business_types
    const userData = data.user || data;
    if (userData?.user_business_types) {
      userData.business_types = userData.user_business_types.map((bt: any) => ({
        id: bt.business_types.id,
        name: bt.business_types.name,
        description: bt.business_types.description,
      }));
    }
    set({ user: userData });
    return userData;
  },
  logout: () => {
    tokenStorage.clear();
    set({ user: null });
    if (typeof window !== "undefined") window.location.href = "/";
  },
}));
